/**
 * Attendance Variance Tasks — HR → GM → MD escalation workflow.
 *
 * Route: /hr/attendance-variance
 *
 * What you do here:
 *  1. Generate tasks for a date (creates one PENDING_HR task per worker
 *     who logged less than effective shift hours).
 *  2. HR reviews each pending task: clicks "Action" → picks reason (sick,
 *     machine breakdown, etc.) → adds note → task becomes RESOLVED.
 *  3. Tasks left idle >24h move to ESCALATED_GM (red badge for GM).
 *     Idle >48h → ESCALATED_MD.
 *  4. GM / MD can acknowledge from this page; HR still owns resolution.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'

import {
  attendanceVarianceApi,
  STATUS_COLOR,
  STATUS_LABEL,
  VARIANCE_STATUSES,
  type VarianceReasonCode,
  type VarianceStatus,
  type VarianceTask,
} from '@/features/hr/attendanceVarianceApi'

const { Title, Text } = Typography
const { TextArea } = Input

// Age-since-creation helper. Returns "3h 12m" / "1d 4h" / "just now".
function ageSince(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  const mins = Math.floor(ms / 60_000)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  const restMins = mins % 60
  if (hours < 24) return `${hours}h ${restMins}m`
  const days = Math.floor(hours / 24)
  const restHours = hours % 24
  return `${days}d ${restHours}h`
}

export default function AttendanceVarianceTasksPage() {
  const qc = useQueryClient()

  const [statusFilter, setStatusFilter] = useState<VarianceStatus | 'OPEN' | 'ALL'>('OPEN')
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const [generateDate, setGenerateDate] = useState<Dayjs>(dayjs().subtract(1, 'day'))
  const [actionModal, setActionModal] = useState<VarianceTask | null>(null)
  const [actionForm] = Form.useForm<{ reason_code: VarianceReasonCode; reason_notes?: string; waive?: boolean }>()

  // ── Queries ──────────────────────────────────────────────────────────
  const summaryQ = useQuery({
    queryKey: ['variance-summary'],
    queryFn: attendanceVarianceApi.summary,
    refetchInterval: 60_000, // refresh badges every minute
  })

  const listParams = useMemo(() => {
    const p: Parameters<typeof attendanceVarianceApi.list>[0] = { limit: 500 }
    if (statusFilter !== 'OPEN' && statusFilter !== 'ALL') p.status = statusFilter
    if (dateRange?.[0]) p.on_date_from = dateRange[0].format('YYYY-MM-DD')
    if (dateRange?.[1]) p.on_date_to = dateRange[1].format('YYYY-MM-DD')
    return p
  }, [statusFilter, dateRange])

  const tasksQ = useQuery({
    queryKey: ['variance-tasks', listParams],
    queryFn: () => attendanceVarianceApi.list(listParams),
  })

  const reasonCodesQ = useQuery({
    queryKey: ['variance-reason-codes'],
    queryFn: attendanceVarianceApi.reasonCodes,
    staleTime: Infinity, // static dictionary
  })

  // Client-side filter for the "OPEN" pseudo-status (3 statuses)
  const filteredTasks = useMemo(() => {
    const all = tasksQ.data ?? []
    if (statusFilter === 'OPEN') {
      return all.filter(t =>
        t.status === 'PENDING_HR' || t.status === 'ESCALATED_GM' || t.status === 'ESCALATED_MD'
      )
    }
    return all
  }, [tasksQ.data, statusFilter])

  // ── Mutations ────────────────────────────────────────────────────────
  const generateMut = useMutation({
    mutationFn: () => attendanceVarianceApi.generateForDate(generateDate.format('YYYY-MM-DD')),
    onSuccess: (r) => {
      message.success(
        `Generated ${r.created} task(s) for ${r.on_date}. ` +
        `Checked ${r.employees_checked} employees; ` +
        `skipped ${r.skipped_existing} existing, ` +
        `${r.skipped_no_punches_absent} absent, ` +
        `${r.skipped_within_tolerance} on-target.`,
        8,
      )
      qc.invalidateQueries({ queryKey: ['variance-tasks'] })
      qc.invalidateQueries({ queryKey: ['variance-summary'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Generate failed'),
  })

  const escalateMut = useMutation({
    mutationFn: attendanceVarianceApi.escalatePending,
    onSuccess: (r) => {
      message.success(
        `Promoted ${r.promoted_to_gm} → GM, ${r.promoted_to_md} → MD. ` +
        `${r.total_open} task(s) still open.`,
      )
      qc.invalidateQueries({ queryKey: ['variance-tasks'] })
      qc.invalidateQueries({ queryKey: ['variance-summary'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Escalate failed'),
  })

  const actionMut = useMutation({
    mutationFn: (vars: { id: string; body: Parameters<typeof attendanceVarianceApi.action>[1] }) =>
      attendanceVarianceApi.action(vars.id, vars.body),
    onSuccess: () => {
      message.success('Task closed')
      setActionModal(null)
      actionForm.resetFields()
      qc.invalidateQueries({ queryKey: ['variance-tasks'] })
      qc.invalidateQueries({ queryKey: ['variance-summary'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Action failed'),
  })

  const gmAckMut = useMutation({
    mutationFn: attendanceVarianceApi.gmAcknowledge,
    onSuccess: () => {
      message.success('GM acknowledged')
      qc.invalidateQueries({ queryKey: ['variance-tasks'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'GM ack failed'),
  })

  const mdAckMut = useMutation({
    mutationFn: attendanceVarianceApi.mdAcknowledge,
    onSuccess: () => {
      message.success('MD acknowledged')
      qc.invalidateQueries({ queryKey: ['variance-tasks'] })
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'MD ack failed'),
  })

  // ── Submit ───────────────────────────────────────────────────────────
  const submitAction = (waive: boolean) => {
    actionForm.validateFields().then((vals) => {
      if (!actionModal) return
      actionMut.mutate({
        id: actionModal.id,
        body: {
          reason_code: vals.reason_code,
          reason_notes: vals.reason_notes,
          waive,
        },
      })
    })
  }

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        Attendance Variance Tasks
      </Title>
      <Text type="secondary">
        Workers logged shorter than effective shift hours. HR must capture a reason — idle &gt;24h escalates to GM, &gt;48h to MD.
      </Text>

      {/* Summary widgets */}
      <Row gutter={16} style={{ marginTop: 16, marginBottom: 16 }}>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Pending HR"
              value={summaryQ.data?.pending_hr ?? '—'}
              valueStyle={{ color: '#d4b106' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Escalated GM"
              value={summaryQ.data?.escalated_gm ?? '—'}
              valueStyle={{ color: '#fa8c16' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Escalated MD"
              value={summaryQ.data?.escalated_md ?? '—'}
              valueStyle={{ color: '#f5222d' }}
              prefix={<ExclamationCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Resolved"
              value={summaryQ.data?.resolved ?? '—'}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Waived"
              value={summaryQ.data?.waived ?? '—'}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small">
            <Statistic
              title="Total Open"
              value={summaryQ.data?.total_open ?? '—'}
              valueStyle={{ color: '#1677ff' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Generate + Escalate actions */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text strong>Generate tasks for:</Text>
          <DatePicker
            value={generateDate}
            onChange={(d) => d && setGenerateDate(d)}
            allowClear={false}
            disabledDate={(d) => d.isAfter(dayjs(), 'day')}
          />
          <Popconfirm
            title={`Generate variance tasks for ${generateDate.format('DD MMM YYYY')}?`}
            description="One task per worker who worked less than the effective shift hours. Re-running is safe."
            onConfirm={() => generateMut.mutate()}
            okText="Generate"
          >
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={generateMut.isPending}
            >
              Generate Now
            </Button>
          </Popconfirm>
          <Tooltip title="Promote PENDING_HR → GM after 24h, GM → MD after 48h. Safe to run any time.">
            <Popconfirm
              title="Tick the escalation state machine now?"
              onConfirm={() => escalateMut.mutate()}
              okText="Run"
            >
              <Button
                icon={<ThunderboltOutlined />}
                loading={escalateMut.isPending}
              >
                Run Escalation
              </Button>
            </Popconfirm>
          </Tooltip>
        </Space>
      </Card>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text>Status:</Text>
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            style={{ width: 200 }}
            options={[
              { label: 'Open (HR + GM + MD)', value: 'OPEN' },
              { label: 'All statuses', value: 'ALL' },
              ...VARIANCE_STATUSES.map(s => ({ label: STATUS_LABEL[s], value: s })),
            ]}
          />
          <Text>Date range:</Text>
          <DatePicker.RangePicker
            value={dateRange as any}
            onChange={(r) => setDateRange(r as any)}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => qc.invalidateQueries({ queryKey: ['variance-tasks'] })}
          >
            Refresh
          </Button>
        </Space>
      </Card>

      {/* Table */}
      <Card>
        {filteredTasks.length === 0 && !tasksQ.isFetching ? (
          <Empty
            description={
              statusFilter === 'OPEN'
                ? 'No open variance tasks. Either nobody clocked short or HR has already closed everything.'
                : 'No tasks match the current filter.'
            }
          />
        ) : (
          <Table<VarianceTask>
            dataSource={filteredTasks}
            rowKey="id"
            loading={tasksQ.isLoading}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            size="small"
            columns={[
              {
                title: 'Date',
                dataIndex: 'on_date',
                width: 100,
                sorter: (a, b) => (a.on_date ?? '').localeCompare(b.on_date ?? ''),
                defaultSortOrder: 'descend',
                render: (d) => d ? dayjs(d).format('DD MMM') : '—',
              },
              {
                title: 'Employee',
                key: 'emp',
                width: 240,
                render: (_, t) => (
                  <Space direction="vertical" size={0}>
                    <Text strong>{t.emp_name ?? '—'}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t.emp_code ?? 'no code'} · BIO {t.biometric_id ?? '—'}
                    </Text>
                  </Space>
                ),
              },
              {
                title: 'Department',
                dataIndex: 'department_name',
                width: 140,
                render: (d) => d ?? '—',
              },
              {
                title: 'Hours',
                key: 'hours',
                width: 140,
                render: (_, t) => (
                  <Space direction="vertical" size={0}>
                    <Text>
                      <Text strong style={{ color: '#cf1322' }}>{t.actual_hours?.toFixed(2) ?? '—'}h</Text>
                      <Text type="secondary"> / {t.effective_shift_hours?.toFixed(2) ?? '—'}h</Text>
                    </Text>
                    <Text type="danger" style={{ fontSize: 12 }}>
                      {(t.variance_hours ?? 0).toFixed(2)}h gap
                    </Text>
                  </Space>
                ),
              },
              {
                title: 'In / Out',
                key: 'in_out',
                width: 130,
                render: (_, t) => (
                  <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
                    <Text>In: {t.first_in_time ? dayjs(t.first_in_time).format('HH:mm') : '—'}</Text>
                    <Text>Out: {t.last_out_time ? dayjs(t.last_out_time).format('HH:mm') : '—'}</Text>
                    <Text type="secondary">{t.punches_count ?? 0} punches</Text>
                  </Space>
                ),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 140,
                filters: VARIANCE_STATUSES.map(s => ({ text: STATUS_LABEL[s], value: s })),
                onFilter: (v, r) => r.status === v,
                render: (s: VarianceStatus, t) => (
                  <Space direction="vertical" size={0}>
                    <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>
                    {t.gm_acknowledged_at && (
                      <Tag color="orange" style={{ fontSize: 10 }}>GM ✓</Tag>
                    )}
                    {t.md_acknowledged_at && (
                      <Tag color="red" style={{ fontSize: 10 }}>MD ✓</Tag>
                    )}
                  </Space>
                ),
              },
              {
                title: 'Age',
                key: 'age',
                width: 90,
                sorter: (a, b) =>
                  new Date(a.created_at ?? 0).getTime() - new Date(b.created_at ?? 0).getTime(),
                render: (_, t) => (
                  <Tooltip title={t.created_at ? dayjs(t.created_at).format('DD MMM HH:mm') : ''}>
                    <Text type={
                      ageSince(t.created_at).includes('d') ? 'danger' : undefined
                    }>
                      {ageSince(t.created_at)}
                    </Text>
                  </Tooltip>
                ),
              },
              {
                title: 'Reason',
                key: 'reason',
                width: 200,
                render: (_, t) => t.reason_code ? (
                  <Tooltip title={t.reason_notes ?? ''}>
                    <Tag>{t.reason_code}</Tag>
                    {t.reason_notes && (
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }} ellipsis>
                        {t.reason_notes.slice(0, 40)}{t.reason_notes.length > 40 ? '…' : ''}
                      </Text>
                    )}
                  </Tooltip>
                ) : <Text type="secondary" style={{ fontSize: 12 }}>—</Text>,
              },
              {
                title: 'Action',
                key: 'action',
                fixed: 'right',
                width: 180,
                render: (_, t) => {
                  const isOpen = t.status === 'PENDING_HR' || t.status === 'ESCALATED_GM' || t.status === 'ESCALATED_MD'
                  return (
                    <Space size={4}>
                      {isOpen && (
                        <Button
                          size="small"
                          type="primary"
                          onClick={() => {
                            setActionModal(t)
                            actionForm.resetFields()
                          }}
                        >
                          Action
                        </Button>
                      )}
                      {t.status === 'ESCALATED_GM' && !t.gm_acknowledged_at && (
                        <Button size="small" onClick={() => gmAckMut.mutate(t.id)}>
                          GM Ack
                        </Button>
                      )}
                      {t.status === 'ESCALATED_MD' && !t.md_acknowledged_at && (
                        <Button size="small" danger onClick={() => mdAckMut.mutate(t.id)}>
                          MD Ack
                        </Button>
                      )}
                    </Space>
                  )
                },
              },
            ]}
            scroll={{ x: 1400 }}
          />
        )}
      </Card>

      {/* Action modal */}
      <Modal
        open={!!actionModal}
        title={
          actionModal ? (
            <>
              Capture reason — {actionModal.emp_name} ({actionModal.emp_code ?? 'no code'})
            </>
          ) : ''
        }
        onCancel={() => { setActionModal(null); actionForm.resetFields() }}
        footer={[
          <Button key="cancel" onClick={() => { setActionModal(null); actionForm.resetFields() }}>
            Cancel
          </Button>,
          <Button
            key="waive"
            onClick={() => submitAction(true)}
            loading={actionMut.isPending}
          >
            Waive (no action needed)
          </Button>,
          <Button
            key="resolve"
            type="primary"
            onClick={() => submitAction(false)}
            loading={actionMut.isPending}
          >
            Resolve
          </Button>,
        ]}
        destroyOnClose
      >
        {actionModal && (
          <>
            <Alert
              type="info"
              showIcon
              message={
                <>
                  Worked <strong>{actionModal.actual_hours?.toFixed(2)}h</strong> of expected{' '}
                  <strong>{actionModal.effective_shift_hours?.toFixed(2)}h</strong>{' '}
                  on {dayjs(actionModal.on_date).format('DD MMM YYYY')}.
                  Punches: {actionModal.punches_count ?? 0}.
                </>
              }
              style={{ marginBottom: 16 }}
            />
            <Form form={actionForm} layout="vertical">
              <Form.Item
                name="reason_code"
                label="Reason"
                rules={[{ required: true, message: 'Pick a reason' }]}
              >
                <Select
                  placeholder="Why did the worker log short hours?"
                  options={(reasonCodesQ.data?.codes ?? []).map(c => ({
                    label: reasonCodesQ.data?.labels[c] ?? c,
                    value: c,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="reason_notes"
                label="Notes (what HR captured on the call)"
              >
                <TextArea
                  rows={3}
                  placeholder="e.g. Worker said fever since morning, will bring medical certificate tomorrow."
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <strong>Resolve</strong> = reason captured, ticket closed.
                {' '}<strong>Waive</strong> = no action needed (e.g. pre-approved early leave).
              </Text>
            </Form>
          </>
        )}
      </Modal>
    </div>
  )
}
